import React from "react";
import {
  AlertTriangle as WarningIcon,
  Check as CheckIcon,
  X as CloseIcon,
} from "lucide-react";

const NotificationContext = React.createContext();

const Notification = ({ message, severity, onClose }) => {
  const severityClass = {
    success: "bg-semantic-completed text-black",
    error: "bg-red-600 text-white",
    warning: "bg-semantic-inProgress text-black",
    info: "bg-brand-action text-brand-action-text",
  }[severity] || "bg-accent-purple text-white";

  return (
    <div
      className={`${severityClass} pointer-events-auto min-w-80 max-w-md px-4 py-3.5 rounded-2xl border shadow-lg shadow-black/5 dark:shadow-black/20 backdrop-blur-xl backdrop-saturate-150 flex items-start gap-3`}
    >
      {severity === "success" && <CheckIcon size={18} className="shrink-0" />}
      {(severity === "error" || severity === "warning") && (
        <WarningIcon size={18} className="shrink-0" />
      )}
      <p className="flex-1 min-w-0 text-sm font-medium leading-relaxed">{message}</p>
      <button
        onClick={onClose}
        className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center opacity-60 hover:opacity-100 transition-all duration-150 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-current/20"
        aria-label="Close notification"
      >
        <CloseIcon size={14} />
      </button>
    </div>
  );
};

export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = React.useState([]);

  const addNotification = React.useCallback((notification) => {
    const id = Date.now() + Math.random();
    setNotifications((current) => [...current, { ...notification, id }]);

    const duration = notification.duration || 2000;
    if (duration > 0) {
      setTimeout(() => {
        setNotifications((current) => current.filter((item) => item.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const removeNotification = React.useCallback((id) => {
    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  const value = React.useMemo(
    () => ({ addNotification, removeNotification, notifications }),
    [addNotification, removeNotification, notifications],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="flex flex-col items-center pt-6 gap-3">
          {notifications.map((notification) => (
            <Notification
              key={notification.id}
              message={notification.message}
              severity={notification.severity}
              onClose={() => removeNotification(notification.id)}
            />
          ))}
        </div>
      </div>
    </NotificationContext.Provider>
  );
};

export const useToast = () => {
  const { addNotification } = useNotifications();

  return React.useMemo(
    () => ({
      success: (message, options = {}) => addNotification({ message, severity: "success", ...options }),
      error: (message, options = {}) => addNotification({ message, severity: "error", ...options }),
      warning: (message, options = {}) => addNotification({ message, severity: "warning", ...options }),
      info: (message, options = {}) => addNotification({ message, severity: "info", ...options }),
    }),
    [addNotification],
  );
};
